import React, { useState, useEffect } from 'react';
import { Modal, Form, InputNumber, Button, message, Row, Col, Card, Typography, Space, Tooltip, Spin } from 'antd';
import { User } from '../../../../types/user.types';
import { LeaveLimits, LeaveType } from '../../../../types/leaves.types';
import userService from '../../../../services/user/user.service';
import leaveService from '../../../../services/leave/leave.service';
import { useTranslation } from 'react-i18next';
import { handleApiError } from '../../../../utils/errorHandler';

const { Title, Text } = Typography;

interface LeaveLimitsModalProps {
  open: boolean;
  onClose: () => void;
  user: User | null;
  onSuccess?: () => void;
}

const LeaveLimitsModal: React.FC<LeaveLimitsModalProps> = ({
  open,
  onClose,
  user,
  onSuccess
}) => {
  const { t } = useTranslation();
  const [form] = Form.useForm();
  const [loading, setLoading] = useState(false);
  const [leaveBalance, setLeaveBalance] = useState<any>(null);
  const [balanceLoading, setBalanceLoading] = useState(false);

  // İzin türleri ve Türkçe karşılıkları
  // Partial kullanarak tüm LeaveType değerlerinin zorunlu olmamasını sağlıyoruz
  const leaveTypeLabels: Partial<Record<LeaveType, string>> = {
    [LeaveType.ANNUAL]: 'Yıllık İzin',
    [LeaveType.HOURLY]: 'Saatlik İzin',
    [LeaveType.MEDICAL]: 'Sağlık İzni',
    [LeaveType.MARRIAGE]: 'Evlilik İzni',
    [LeaveType.BIRTH]: 'Doğum İzni',
    [LeaveType.UNPAID]: 'Ücretsiz İzin',
    [LeaveType.PATERNITY]: 'Babalık İzni',
    [LeaveType.DEATH]: 'Ölüm İzni'
  };

  // İzin türleri ikonları
  // Partial kullanarak tüm LeaveType değerlerinin zorunlu olmamasını sağlıyoruz
  const leaveTypeIcons: Partial<Record<LeaveType, string>> = {
    [LeaveType.ANNUAL]: 'ti-calendar-time',
    [LeaveType.HOURLY]: 'ti-clock',
    [LeaveType.MEDICAL]: 'ti-medical-cross',
    [LeaveType.MARRIAGE]: 'ti-heart',
    [LeaveType.BIRTH]: 'ti-baby-carriage',
    [LeaveType.UNPAID]: 'ti-currency-off',
    [LeaveType.PATERNITY]: 'ti-baby-carriage',
    [LeaveType.DEATH]: 'ti-heart'
  };

  // İzin türleri renkleri
  // Partial kullanarak tüm LeaveType değerlerinin zorunlu olmamasını sağlıyoruz
  const leaveTypeColors: Partial<Record<LeaveType, string>> = {
    [LeaveType.ANNUAL]: '#4f46e5',
    [LeaveType.HOURLY]: '#8b5cf6',
    [LeaveType.MEDICAL]: '#ef4444',
    [LeaveType.MARRIAGE]: '#ec4899',
    [LeaveType.BIRTH]: '#f59e0b',
    [LeaveType.UNPAID]: '#6b7280',
    [LeaveType.PATERNITY]: '#10b981',
    [LeaveType.DEATH]: '#f97316'
  };

  useEffect(() => {
    if (open && user) {
      // Kullanıcının mevcut izin limitlerini form'a yükle
      const currentLimits = user.leaveLimits || {};
      form.setFieldsValue(currentLimits);
      
      // İzin bakiyesini API'den çek
      fetchLeaveBalance();
    }
  }, [open, user, form]);

  // İzin bakiyesi verilerini API'den çeken fonksiyon
  const fetchLeaveBalance = async () => {
    if (!user) return;
    
    try {
      setBalanceLoading(true);
      const response = await leaveService.getComprehensiveLeaveBalance(user.id);
      // apiService zaten response.data.data'yı extract ediyor, direkt leaveBalance'e erişebiliriz
      const apiBalance = response?.leaveBalance || {};
      setLeaveBalance(apiBalance);

      // Yıllık izin limiti alanını gerçek yıllık HAK oranından (user.annualLeaveLimit) doldur.
      // apiBalance.annual.limit burada KULLANILAMAZ: o "kalan + kullanılan" birikimli/türetilmiş
      // bir sayı, yıllık hak oranı değil — onu forma koyup kaydetmek annualLeaveLimit'i (örn.
      // 14) modalı sadece açıp kaydetmekle bile yanlış bir değere (çoğu zaman 0'a) ezerdi.
      if (user.annualLeaveLimit !== undefined && user.annualLeaveLimit !== null) {
        form.setFieldValue(LeaveType.ANNUAL, user.annualLeaveLimit);
      }
    } catch (error: any) {
      console.error('İzin bakiyesi yüklenirken hata:', error);
      // Hata durumunda boş obje kullan (kart yine de gösterilsin)
      setLeaveBalance({});
    } finally {
      setBalanceLoading(false);
    }
  };

  const handleSubmit = async () => {
    if (!user) return;

    try {
      setLoading(true);
      const values = await form.validateFields();
      
      // Yıllık izin limitini çalışan güncelleme endpoint'ine gönder
      const annualLeaveLimitValue = values[LeaveType.ANNUAL];

      // Diğer izin türlerini mevcut leave-limits endpoint'ine gönder
      const leaveLimits: Partial<LeaveLimits> = {};
      Object.keys(values).forEach(key => {
        if (
          key !== LeaveType.ANNUAL &&
          values[key] !== undefined &&
          values[key] !== null
        ) {
          leaveLimits[key as keyof LeaveLimits] = values[key];
        }
      });

      const updateRequests: Promise<any>[] = [];

      if (annualLeaveLimitValue !== undefined && annualLeaveLimitValue !== null) {
        updateRequests.push(
          userService.updateUserAdmin(user.id, { annualLeaveLimit: annualLeaveLimitValue })
        );
      }

      if (Object.keys(leaveLimits).length > 0) {
        updateRequests.push(userService.updateUserLeaveLimits(user.id, leaveLimits));
      }

      if (updateRequests.length > 0) {
        await Promise.all(updateRequests);
      }
      
      message.success('İzin limitleri başarıyla güncellendi');
      
      // Limit güncellendiğinde bakiyeyi yeniden yükle
      await fetchLeaveBalance();
      
      onSuccess?.();
      onClose();
      
    } catch (error: any) {
      handleApiError(error, t);
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    form.resetFields();
    onClose();
  };

  // Saatlik izin için birim belirleme fonksiyonu
  // Sadece saatlik izin için saat, diğerleri için gün kullanılır
  const getUnitForLeaveType = (leaveType: LeaveType): 'Saat' | 'Gün' => {
    if (leaveType === LeaveType.HOURLY) {
      return 'Saat';
    }
    return 'Gün';
  };

  // Maksimum değer belirleme fonksiyonu
  // Sadece saatlik izin için daha yüksek maksimum değer (saat cinsinden)
  const getMaxValueForLeaveType = (leaveType: LeaveType): number => {
    if (leaveType === LeaveType.HOURLY) {
      return 8760; // 365 gün * 24 saat = 8760 saat
    }
    return 365; // Diğer izin türleri için maksimum 365 gün
  };

  if (!user) return null;

  return (
    <Modal
      title={
        <div className="d-flex align-items-center">
          <div className="bg-primary rounded-circle p-2 d-flex align-items-center justify-content-center me-3">
            <i className="ti ti-calendar-time text-white" style={{ fontSize: '18px' }} />
          </div>
          <div>
            <Title level={5} className="mb-0">İzin Limitleri Yönetimi</Title>
            <Text type="secondary" className="small">
              {user.firstName} {user.lastName}
            </Text>
          </div>
        </div>
      }
      open={open}
      onCancel={handleCancel}
      width={800}
      footer={[
        <Button key="cancel" onClick={handleCancel}>
          İptal
        </Button>,
        <Button 
          key="submit" 
          type="primary" 
          loading={loading}
          onClick={handleSubmit}
        >
          <i className="ti ti-device-floppy me-1" />
          Kaydet
        </Button>
      ]}
      destroyOnClose
    >
      <div className="leave-limits-form">
        <div className="alert alert-info mb-4 d-flex align-items-center">
          <i className="ti ti-info-circle me-2 fs-5"></i>
          <div>
            <strong>Bilgi:</strong> Bu ekrandan tüm izin limitlerini yönetebilirsiniz. Saatlik izin için limit saat cinsinden belirlenir.
          </div>
        </div>

        <Form
          form={form}
          layout="vertical"
          className="leave-limits-grid"
        >
          <Row gutter={[16, 16]}>
            {Object.values(LeaveType)
              .filter(leaveType => 
                leaveType !== LeaveType.SPECIAL   // Özel izni filtrele (kaldırıldı)
              ) 
              .map((leaveType) => {
                const unit = getUnitForLeaveType(leaveType);
                const maxValue = getMaxValueForLeaveType(leaveType);
                
                // İzin bakiyesi bilgilerini al (varsa)
                const balance = leaveBalance?.[leaveType];
                const hasBalance = balance && !balanceLoading;
                
                return (
              <Col xs={24} sm={12} md={8} key={leaveType}>
                <Card 
                  size="small" 
                  className="leave-type-card h-100"
                  style={{ 
                    borderLeft: `4px solid ${leaveTypeColors[leaveType]}`,
                    transition: 'all 0.3s ease'
                  }}
                  bodyStyle={{ padding: '16px' }}
                >
                  <div className="d-flex align-items-center mb-3">
                    <div 
                      className="rounded-circle p-2 me-2 d-flex align-items-center justify-content-center"
                      style={{ 
                        backgroundColor: `${leaveTypeColors[leaveType]}15`,
                        width: '32px',
                        height: '32px'
                      }}
                    >
                      <i 
                        className={leaveTypeIcons[leaveType]} 
                        style={{ 
                          color: leaveTypeColors[leaveType],
                          fontSize: '14px'
                        }}
                      />
                    </div>
                    <div className="flex-1">
                      <Text strong className="d-block" style={{ fontSize: '13px' }}>
                        {leaveTypeLabels[leaveType]}
                      </Text>
                      <Text type="secondary" style={{ fontSize: '11px' }}>
                        {unit}
                      </Text>
                    </div>
                  </div>

                  {/* İzin kullanım bilgisi - API'den gelen veriler */}
                  {balanceLoading ? (
                    <div className="text-center mb-2" style={{ padding: '8px 0' }}>
                      <Spin size="small" />
                    </div>
                  ) : hasBalance ? (
                    <div 
                      className="mb-2 p-2 rounded"
                      style={{ 
                        backgroundColor: `${leaveTypeColors[leaveType]}08`,
                        border: `1px solid ${leaveTypeColors[leaveType]}20`
                      }}
                    >
                      <div className="d-flex justify-content-between align-items-center">
                        <Text style={{ fontSize: '11px', color: '#666' }}>
                          Kullanılan:
                        </Text>
                        <Text strong style={{ fontSize: '12px', color: leaveTypeColors[leaveType] }}>
                          {balance.used || 0} {balance.unit || unit}
                        </Text>
                      </div>
                      <div className="d-flex justify-content-between align-items-center mt-1">
                        <Text style={{ fontSize: '11px', color: '#666' }}>
                          Kalan:
                        </Text>
                        <Text strong style={{ fontSize: '12px', color: '#52c41a' }}>
                          {balance.remaining || 0} {balance.unit || unit}
                        </Text>
                      </div>
                    </div>
                  ) : (
                    <div className="mb-2 p-2 rounded text-center" style={{ backgroundColor: '#f5f5f5' }}>
                      <Text type="secondary" style={{ fontSize: '10px' }}>
                        Kullanım bilgisi yükleniyor...
                      </Text>
                    </div>
                  )}

                  <Form.Item
                    name={leaveType}
                    className="mb-0"
                    label={<Text style={{ fontSize: '11px' }}> Limit</Text>}
                    rules={[
                      {
                        type: 'number',
                        min: 0,
                        message: 'Değer 0\'dan küçük olamaz'
                      }
                    ]}
                  >
                    <InputNumber
                      placeholder="Limit"
                      min={0}
                      max={maxValue}
                      style={{ width: '100%' }}
                      size="small"
                      addonAfter={unit}
                    />
                  </Form.Item>
                </Card>
              </Col>
            );
              })}
          </Row>
        </Form>

        {/* <div className="mt-4">
          <Card size="small" className="bg-light">
            <div className="d-flex align-items-center">
              <i className="ti ti-bulb text-warning me-2 fs-5"></i>
              <div>
                <Text strong className="d-block">İpuçları:</Text>
                <ul className="mb-0 mt-2" style={{ paddingLeft: '20px' }}>
                  <li><Text type="secondary" style={{ fontSize: '12px' }}>Yıllık izin genellikle 14-30 gün arasında belirlenir</Text></li>
                  <li><Text type="secondary" style={{ fontSize: '12px' }}>Sağlık izni sınırsız olabilir (0 = sınırsız)</Text></li>
                  <li><Text type="secondary" style={{ fontSize: '12px' }}>Evlilik ve doğum izinleri yasal düzenlemelere göre belirlenir</Text></li>
                </ul>
              </div>
            </div>
          </Card>
        </div> */}
      </div>

      <style>{`
        .leave-type-card {
          position: relative;
          overflow: hidden;
        }
        
        .leave-type-card:hover {
          transform: translateY(-2px);
          box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
        }
        
        .leave-limits-grid .ant-form-item {
          margin-bottom: 0;
        }
        
        .leave-limits-grid .ant-form-item-label {
          padding-bottom: 2px;
        }
        
        .leave-limits-grid .ant-form-item-label > label {
          height: auto;
        }
        
        .leave-limits-grid .ant-input-number {
          border-radius: 6px;
        }
        
        .leave-limits-grid .ant-input-number:focus,
        .leave-limits-grid .ant-input-number-focused {
          border-color: var(--bs-primary);
          box-shadow: 0 0 0 2px rgba(79, 70, 229, 0.1);
        }
        
        /* İzin kullanım bilgisi animasyonu */
        .leave-type-card .mb-2.p-2 {
          transition: all 0.2s ease;
        }
        
        .leave-type-card:hover .mb-2.p-2 {
          transform: scale(1.02);
        }
      `}</style>
    </Modal>
  );
};

export default LeaveLimitsModal;
